/* eslint-disable no-loop-func */
import React, { useEffect, useRef, useState, useCallback } from "react";
import classNames from "classnames";
import { Card, Col, Row, Spinner, InputGroup, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPaperPlane, 
  faUser, 
  faPlus,
  faClock,
  faMessage,
  faBolt,
  faLightbulb,
  faSearch,
  faCircle,
  faBullhorn,
  faChartLine,
  faUsers
} from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import Backdrop from "components/common/Backdrop";
import axiosInstance from "helpers/api";
import { useAtom } from "jotai";
import { user_info } from "jotai/UserJotaiInstance";
import toast from "react-hot-toast";
import { formatErrorForReact } from 'helpers/utils';
import { UserInfo } from "interfaces/user";

interface Message {
  id: number;
  text: string;
  sender: "user" | "assistant" | "collaborator";
  timestamp?: Date;
  type?: "text" | "insight" | "analysis";
  senderName?: string;
  lastUpdated?: number; // For forcing React re-renders during streaming
}

interface AgentType {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  type: 'ai' | 'human';
  systemPrompt?: string;
  userId?: string;
  gradient: string;
}

interface Collaborator {
  collaborator_id: string;
  invitee: {
    collaborator_first_name: string;
    collaborator_last_name: string;
    collaborator_email: string;
    collaborator_phone: string;
  };
  created_on: string;
}

interface EnhancedConversation {
  id: number;
  name: string;
  lastMessage: string;
  timestamp: Date;
  unread?: boolean;
  agentType: string;
  participantId?: string;
  participantName?: string;
  systemPrompt?: string;
}

// AI Agent Definitions
const aiAgents: AgentType[] = [
  {
    id: 'marketing-ai',
    name: 'Marketing Agent',
    description: 'Marketing strategies and campaign optimization for real estate',
    icon: faBullhorn,
    color: 'primary',
    type: 'ai',
    systemPrompt: 'You are a friendly marketing coach for real estate agents. Keep responses conversational and under 3-4 sentences. Ask follow-up questions to understand their specific situation. Focus on one key insight at a time rather than overwhelming them with information. Be encouraging and practical.',
    gradient: 'linear-gradient(135deg, #0d6efd, #6610f2)'
  },
  {
    id: 'lead-analysis-ai',
    name: 'Lead Analysis Agent',
    description: 'Lead scoring, analysis, and task recommendations',
    icon: faChartLine,
    color: 'success',
    type: 'ai',
    systemPrompt: 'You are a supportive lead analysis coach for real estate agents. Give brief, actionable insights in 2-3 sentences. Always ask what specific challenge they want to tackle next. Focus on one clear next step rather than long lists. Be conversational and encouraging.',
    gradient: 'linear-gradient(135deg, #198754, #20c997)'
  },
  {
    id: 'general-ai',
    name: 'General Assistant',
    description: 'General CRM optimization and workflow assistance',
    icon: faBolt,
    color: 'info',
    type: 'ai',
    systemPrompt: 'You are a helpful business coach for real estate professionals. Keep responses short and conversational - aim for 2-4 sentences max. Ask questions to understand their specific needs. Provide one focused tip at a time. Be supportive and encouraging like a good coach.',
    gradient: 'linear-gradient(135deg, #0dcaf0, #6f42c1)'
  }
];

const dummyConversationHistory: EnhancedConversation[] = [
  {
    id: 1,
    name: "Marketing Strategy Discussion",
    lastMessage: "What's the best pricing strategy for my new listing?",
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    unread: false,
    agentType: "marketing-ai"
  },
  {
    id: 2,
    name: "Lead Analysis Session",
    lastMessage: "Analyze my recent lead quality trends",
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    unread: true,
    agentType: "lead-analysis-ai"
  }
];

const Chat = ({ className }: any) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: "👋 Hello! Welcome to **KAIROS IQ AI Assistant**. I'm here to help you with lead analysis, market insights, and CRM optimization. What would you like to explore today?",
      sender: "assistant",
      timestamp: new Date(),
      type: "text"
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | any>("");
  const [conversationHistory, setConversationHistory] = useState<EnhancedConversation[]>(dummyConversationHistory);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [activeConversation, setActiveConversation] = useState<EnhancedConversation | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [showChat, setShowChat] = useState(true);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  // Fetch collaborators from existing API
  const fetchCollaborators = async () => {
    try {
      const response = await axiosInstance.get("/collaborators");
      setCollaborators(response.data.data.collaborators);
    } catch (error: any) {
      console.log("Error fetching collaborators:", error.response?.data?.message);
    }
  };

  useEffect(() => {
    fetchCollaborators();
  }, []);

  // Polling system for human collaborator messages
  const startPolling = (conversationId: number) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    const interval = setInterval(async () => {
      if (activeConversation?.agentType === 'human-collaborator') {
        try {
          // Poll for new messages from human collaborators
          const response = await axiosInstance.get(`/conversations/${conversationId}/messages?after=${Date.now() - 5000}`);
          if (response.data.newMessages?.length > 0) {
            const newMessages = response.data.newMessages.map((msg: any) => ({
              id: msg.id,
              text: msg.content,
              sender: "collaborator",
              timestamp: new Date(msg.timestamp),
              type: "text",
              senderName: msg.senderName
            }));
            setMessages(prev => [...prev, ...newMessages]);
          }
        } catch (error) {
          // Silently handle polling errors
        }
      }
    }, 3000); // Poll every 3 seconds
    
    setPollingInterval(interval);
  };

  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);

  useEffect(() => {
    return () => stopPolling(); // Cleanup on unmount
  }, [stopPolling]);

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowChat(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getConversationStyling = (agentType: string) => {
    const agent = aiAgents.find(a => a.id === agentType);
    if (agent) return agent;
    
    switch (agentType) {
      case 'human-collaborator':
        return { icon: faUsers, color: 'warning', gradient: 'linear-gradient(135deg, #ffc107, #fd7e14)' };
      default:
        return { icon: faBolt, color: 'secondary', gradient: 'linear-gradient(135deg, #6c757d, #495057)' };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      setError("Message cannot be empty");
      return;
    }

    if (!activeConversation) {
      setError("Please select a conversation or start a new one");
      return;
    }

    setError("");
    setLoading(true);

    const newUserMessage: Message = {
      id: messages.length + 1,
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
      type: "text"
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    const messageToSend = inputMessage;
    setInputMessage("");

    try {
      if (activeConversation.agentType === 'human-collaborator') {
        // Send message to human collaborator (no streaming)
        await axiosInstance.post('/chat/team/chat', {
          conversation_id: activeConversation.id.toString(),
          message: messageToSend,
          participant_id: activeConversation.participantId,
        });

        toast.success("Message sent to team member");
        setLoading(false);
      } else {
        // Use streaming for AI agents
        await handleStreamingMessage(messageToSend);
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.error || "Failed to send message");
      setLoading(false);
    }
  };

  // PRODUCTION READY: Streaming message handler 
  const handleStreamingMessage = async (messageToSend: string) => {
    try {
      // Create a placeholder message for the AI response
      const aiResponsePlaceholder: Message = {
        id: Date.now(),
        text: "Thinking...",
        sender: "assistant",
        timestamp: new Date(),
        type: "insight"
      };

      // Add placeholder to messages
      setMessages((prevMessages) => [...prevMessages, aiResponsePlaceholder]);

      // Prepare request payload
      const payload = {
        conversation_id: activeConversation!.id.toString(),
        message: messageToSend,
        agent_type: activeConversation!.agentType,
        system_prompt: activeConversation!.systemPrompt,
      };

      // Use fetch for streaming
      const response = await fetch(`${axiosInstance.defaults.baseURL}chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'chunk' && data.partial_response) {
                    fullText = data.partial_response;
                    
                    // Update the message with streaming text
                    setMessages((prevMessages) => {
                      const newMessages = [...prevMessages];
                      const targetIndex = newMessages.findIndex(m => m.id === aiResponsePlaceholder.id);
                      if (targetIndex >= 0) {
                        newMessages[targetIndex] = {
                          ...newMessages[targetIndex],
                          text: fullText
                        };
                      }
                      return newMessages;
                    });
                    
                  } else if (data.type === 'stream_complete') {
                    setLoading(false);
                    return;
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      setLoading(false);

    } catch (error: any) {
      console.error('Error in streaming handler:', error);
      
      // Fallback to regular axios request if streaming fails
      try {
        const payload = {
          conversation_id: activeConversation!.id.toString(),
          message: messageToSend,
          agent_type: activeConversation!.agentType,
          system_prompt: activeConversation!.systemPrompt,
        };

        const axiosResponse = await axiosInstance.post('/chat/chat', payload);
        
        // Update the placeholder with the actual response
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          const targetIndex = newMessages.findIndex(m => m.id === Date.now());
          if (targetIndex >= 0) {
            newMessages[targetIndex] = {
              ...newMessages[targetIndex],
              text: axiosResponse.data.response || "No response received"
            };
          }
          return newMessages;
        });
      } catch (fallbackError) {
        toast.error('Failed to send message');
        // Remove the placeholder AI message on error
        setMessages((prevMessages) => 
          prevMessages.filter(m => m.id !== Date.now())
        );
      }
      
      setLoading(false);
    }
  };

  const getChatHistory = async (conversationId: number) => {
    try {
      setLoadingChatHistory(true);
      const conversation = conversationHistory.find(c => c.id === conversationId);
      setActiveConversation(conversation || null);
      
      // Start polling for human collaborators
      if (conversation?.agentType === 'human-collaborator') {
        startPolling(conversationId);
      } else {
        stopPolling();
      }
      
      // Use simplified API to get messages
      const response = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`);

      const newMessages: Message[] = [];
      response.data.messages?.forEach((message: any) => {
        const newMessage: Message = {
          id: message.id,
          text: message.content,
          sender: message.role === "user" ? "user" : message.role === "collaborator" ? "collaborator" : "assistant",
          timestamp: new Date(message.timestamp),
          type: message.role === "assistant" ? "insight" : "text",
          senderName: message.sender_name
        };
        newMessages.push(newMessage);
      });

      setMessages(newMessages.length > 0 ? newMessages : [
        {
          id: 1,
          text: "👋 Hello! How can I help you today?",
          sender: "assistant",
          timestamp: new Date(),
          type: "text"
        }
      ]);
      setLoadingChatHistory(false);
      
      // Mark conversation as read
      setConversationHistory(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, unread: false }
            : conv
        )
      );
    } catch (error: any) {
      console.error('Error loading chat history:', error);
      toast.error(error.response?.data?.error || "Failed to load conversation");
      setLoadingChatHistory(false);
    }
  };

  const startNewConversation = () => {
    setShowAgentModal(true);
  };

  // Load conversations from API
  const loadConversations = async () => {
    try {
      const response = await axiosInstance.get('/chat/conversations');
      const apiConversations = response.data.conversations.map((conv: any) => ({
        id: conv.id,
        name: conv.title,
        lastMessage: conv.messages?.[0]?.content || "No messages yet",
        timestamp: new Date(conv.updated_at),
        unread: false, // Could be calculated based on read status
        agentType: conv.agent_type,
        participantId: conv.agent_id,
        participantName: conv.agent_id ? "Team Member" : undefined,
        systemPrompt: conv.system_prompt
      }));
      setConversationHistory(apiConversations);
    } catch (error: any) {
      console.error('Error loading conversations:', error);
      // Keep dummy data as fallback
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const handleSelectAgent = async (agent: AgentType) => {
    try {
      // Create conversation via API
      const response = await axiosInstance.post('/chat/conversations', {
        agent_type: agent.id,
        title: `${agent.name} Session`
      });

      const newConversation: EnhancedConversation = {
        id: response.data.conversation.id,
        name: response.data.conversation.title,
        lastMessage: "New conversation started",
        timestamp: new Date(),
        unread: false,
        agentType: agent.id,
        systemPrompt: agent.systemPrompt
      };

      setConversationHistory(prev => [newConversation, ...prev]);
      setActiveConversation(newConversation);
      setMessages([
        {
          id: 1,
          text: `👋 Hello! I'm your **${agent.name}**. ${agent.description}. How can I assist you today?`,
          sender: "assistant",
          timestamp: new Date(),
          type: "text"
        },
      ]);
      setShowAgentModal(false);
      stopPolling();
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
    }
  };

  const handleSelectCollaborator = async (collaborator: Collaborator) => {
    try {
      const fullName = `${collaborator.invitee.collaborator_first_name} ${collaborator.invitee.collaborator_last_name}`;
      
      // Create conversation via API
      const response = await axiosInstance.post('/chat/conversations', {
        agent_type: 'human-collaborator',
        agent_id: collaborator.collaborator_id,
        title: `Chat with ${fullName}`
      });

      const newConversation: EnhancedConversation = {
        id: response.data.conversation.id,
        name: response.data.conversation.title,
        lastMessage: "New conversation started",
        timestamp: new Date(),
        unread: false,
        agentType: 'human-collaborator',
        participantId: collaborator.collaborator_id,
        participantName: fullName
      };

      setConversationHistory(prev => [newConversation, ...prev]);
      setActiveConversation(newConversation);
      setMessages([
        {
          id: 1,
          text: `👋 Started a new conversation with **${fullName}**. Send a message to begin chatting!`,
          sender: "assistant",
          timestamp: new Date(),
          type: "text"
        },
      ]);
      setShowAgentModal(false);
      startPolling(newConversation.id);
    } catch (error: any) {
      console.error('Error creating team conversation:', error);
      toast.error('Failed to create team conversation');
    }
  };

  const filteredConversations = conversationHistory.filter(conv =>
    conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderMessage = (message: Message) => {
    const isUser = message.sender === "user";
    const isCollaborator = message.sender === "collaborator";
    const isInsight = message.type === "insight" || message.type === "analysis";
    
    const displayText = message.text || "No text";
    
    return (
      <div
        key={`${message.id}-${message.lastUpdated || message.timestamp?.getTime() || 0}`}
        className={classNames(
          "d-flex mb-4",
          isUser ? "justify-content-end" : "justify-content-start"
        )}
      >
        <div className="d-flex align-items-start" style={{ maxWidth: "85%" }}>
          <div className="flex-grow-1">
            <div
              className={classNames(
                "p-3 position-relative shadow-sm",
                isUser 
                  ? "bg-primary text-white" 
                  : isCollaborator
                    ? "bg-warning-subtle border border-warning"
                    : isInsight
                      ? "bg-white border border-success"
                      : "bg-white border"
              )}
              style={{
                borderRadius: isUser ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                boxShadow: isUser 
                  ? "0 4px 12px rgba(13, 110, 253, 0.15)" 
                  : isCollaborator
                    ? "0 4px 12px rgba(255, 193, 7, 0.15)"
                    : isInsight
                      ? "0 4px 12px rgba(25, 135, 84, 0.1)"
                      : "0 4px 12px rgba(0, 0, 0, 0.08)",
                transition: "all 0.2s ease-in-out"
              }}
            >
              {isCollaborator && (
                <div className="d-flex align-items-center mb-2 pb-2 border-bottom border-warning-subtle">
                  <div className="d-flex align-items-center">
                    <div 
                      className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px", background: "linear-gradient(135deg, #ffc107, #fd7e14)" }}
                    >
                      <FontAwesomeIcon icon={faUsers} className="text-white" size="xs" />
                    </div>
                    <small className="text-warning-emphasis fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                      {message.senderName || "TEAM MEMBER"}
                    </small>
                  </div>
                </div>
              )}

              {isInsight && !isCollaborator && (
                <div className="d-flex align-items-center mb-2 pb-2 border-bottom border-success-subtle">
                  <div className="d-flex align-items-center">
                    <div 
                      className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                      style={{ width: "20px", height: "20px", background: "linear-gradient(135deg, #198754, #20c997)" }}
                    >
                      <FontAwesomeIcon icon={faLightbulb} className="text-white" size="xs" />
                    </div>
                    <small className="text-success fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                      AI INSIGHT
                    </small>
                  </div>
                </div>
              )}
              
              <div 
                className={classNames(
                  "lh-base",
                  isUser ? "text-white" : isCollaborator ? "text-warning-emphasis" : "text-body"
                )}
                style={{ fontSize: "0.95rem" }}
              >
                {displayText.includes("**") ? (
                  <div dangerouslySetInnerHTML={{
                    __html: displayText
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                      .replace(/\n/g, '<br>')
                  }} />
                ) : (
                  displayText
                )}
              </div>
              
              {message.timestamp && (
                <div className={classNames(
                  "d-flex align-items-center mt-3 pt-2",
                  isUser ? "border-top border-white-25" : "border-top border-light"
                )}>
                  <FontAwesomeIcon 
                    icon={faClock} 
                    className={classNames(
                      "me-2",
                      isUser ? "text-white-50" : "text-muted"
                    )}
                    size="xs"
                  />
                  <small 
                    className={classNames(
                      isUser ? "text-white-75" : "text-muted"
                    )}
                    style={{ fontSize: "0.7rem", fontWeight: "500" }}
                  >
                    {formatTime(message.timestamp)}
                  </small>
                </div>
              )}
            </div>
          </div>
          
          {isUser && (
            <div 
              className="avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center ms-3 flex-shrink-0 shadow-sm"
              style={{
                background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)",
                border: "2px solid #0d6efd"
              }}
            >
              <FontAwesomeIcon icon={faUser} className="text-white fs-6" />
            </div>
          )}
        </div>
      </div>
    );
  };

  const AgentSelectionModal = () => {
    return (
      <Modal show={showAgentModal} onHide={() => setShowAgentModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Start New Conversation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <h6 className="text-muted mb-3">🤖 AI Assistants</h6>
              {aiAgents.map(agent => (
                <Card key={agent.id} className="mb-2 cursor-pointer border-0 shadow-sm" onClick={() => handleSelectAgent(agent)} style={{ transition: "all 0.2s ease" }} onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"} onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}>
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center">
                      <div 
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center shadow-sm"
                        style={{
                          width: "48px",
                          height: "48px",
                          background: agent.gradient
                        }}
                      >
                        <FontAwesomeIcon icon={agent.icon} className="text-white" />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1 fw-bold">{agent.name}</h6>
                        <small className="text-muted">{agent.description}</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </Col>
            
            <Col xs={12}>
              <h6 className="text-muted mb-3 mt-4">👥 Team Members</h6>
              {collaborators.length > 0 ? (
                collaborators.map(collaborator => (
                  <Card key={collaborator.collaborator_id} className="mb-2 cursor-pointer border-0 shadow-sm" onClick={() => handleSelectCollaborator(collaborator)} style={{ transition: "all 0.2s ease" }} onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"} onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}>
                    <Card.Body className="py-3">
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle me-3 d-flex align-items-center justify-content-center shadow-sm"
                          style={{
                            width: "48px",
                            height: "48px",
                            background: "linear-gradient(135deg, #ffc107, #fd7e14)"
                          }}
                        >
                          <FontAwesomeIcon icon={faUsers} className="text-white" />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold">
                            {collaborator.invitee.collaborator_first_name} {collaborator.invitee.collaborator_last_name}
                          </h6>
                          <small className="text-muted">{collaborator.invitee.collaborator_email}</small>
                          <br />
                          <small className="text-success fw-semibold">● Available for chat</small>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faUsers} className="text-muted mb-2" size="2x" />
                  <p className="text-muted mb-0">No team members available</p>
                  <small className="text-muted">Invite collaborators to start team chats</small>
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      {(isAuthorized || showChat) && (
        <div className="h-100 d-flex flex-column" style={{ backgroundColor: "#f8f9fa" }}>
          <Row className="g-0 h-100">
            {/* Sidebar */}
            <Col xl={3} className="border-end shadow-sm" style={{ backgroundColor: "#ffffff" }}>
              <Card className="h-100 border-0 rounded-0">
                <Card.Header 
                  className="border-bottom-0 py-4"
                  style={{ 
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1 text-white fw-bold">
                        <FontAwesomeIcon icon={faMessage} className="me-2" />
                        Conversations
                      </h6>
                      <small className="text-white-75" style={{ fontSize: "0.8rem" }}>AI Agents & Team Chat</small>
                    </div>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={startNewConversation}
                      className="rounded-pill px-3 fw-semibold"
                      style={{ 
                        fontSize: "0.8rem",
                        boxShadow: "0 2px 8px rgba(255, 255, 255, 0.2)"
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      New
                    </Button>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  {/* Search */}
                  <div className="p-4 border-bottom">
                    <InputGroup>
                      <InputGroup.Text 
                        className="border-end-0 bg-body-secondary"
                        style={{ borderRadius: "12px 0 0 12px" }}
                      >
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-start-0 border-end-0 bg-body-secondary"
                        style={{ 
                          borderRadius: "0 12px 12px 0",
                          fontSize: "0.9rem"
                        }}
                      />
                    </InputGroup>
                  </div>
                  
                  {/* Conversations List */}
                  <div className="conversation-list" style={{ height: "calc(100vh - 300px)", overflowY: "auto" }}>
                    {filteredConversations.map((conversation) => {
                      const styling = getConversationStyling(conversation.agentType);
                      return (
                        <div
                          key={conversation.id}
                          className={classNames(
                            "p-4 border-bottom cursor-pointer conversation-item position-relative",
                            activeConversation?.id === conversation.id ? "bg-primary-subtle" : ""
                          )}
                          onClick={() => getChatHistory(conversation.id)}
                          style={{ 
                            borderLeft: activeConversation?.id === conversation.id ? "4px solid #0d6efd" : "4px solid transparent",
                            transition: "all 0.2s ease-in-out",
                            background: activeConversation?.id === conversation.id 
                              ? "linear-gradient(90deg, rgba(13, 110, 253, 0.08) 0%, rgba(13, 110, 253, 0.02) 100%)"
                              : "transparent"
                          }}
                          onMouseEnter={(e) => {
                            if (activeConversation?.id !== conversation.id) {
                              e.currentTarget.style.backgroundColor = "rgba(13, 110, 253, 0.04)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeConversation?.id !== conversation.id) {
                              e.currentTarget.style.backgroundColor = "transparent";
                            }
                          }}
                        >
                          <div className="d-flex align-items-start">
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0 shadow-sm"
                              style={{
                                width: "44px",
                                height: "44px",
                                background: styling.gradient,
                                border: "2px solid rgba(255, 255, 255, 0.2)"
                              }}
                            >
                              <FontAwesomeIcon 
                                icon={styling.icon} 
                                className="text-white"
                                style={{ fontSize: "0.9rem" }}
                              />
                            </div>
                            
                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 
                                  className="mb-0 text-truncate fw-semibold" 
                                  style={{ 
                                    fontSize: "0.9rem",
                                    color: activeConversation?.id === conversation.id ? "#0d6efd" : "#212529"
                                  }}
                                >
                                  {conversation.name}
                                </h6>
                                {conversation.unread && (
                                  <div 
                                    className="rounded-circle bg-primary"
                                    style={{ width: "8px", height: "8px" }}
                                  />
                                )}
                              </div>
                              
                              <p 
                                className="mb-2 text-muted text-truncate" 
                                style={{ 
                                  fontSize: "0.8rem",
                                  lineHeight: "1.4"
                                }}
                              >
                                {conversation.lastMessage}
                              </p>
                              
                              <div className="d-flex align-items-center text-muted">
                                <FontAwesomeIcon icon={faClock} className="me-1" size="xs" />
                                <small style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                  {formatTime(conversation.timestamp)}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Main Chat Area */}
            <Col xl={9}>
              <Card className="h-100 border-0 rounded-0 shadow-sm">
                <Card.Header 
                  className="border-bottom py-4"
                  style={{ backgroundColor: "#ffffff" }}
                >
                  <div className="d-flex align-items-center">
                    {activeConversation ? (
                      <>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{
                            width: "48px",
                            height: "48px",
                            background: getConversationStyling(activeConversation.agentType).gradient,
                            border: "3px solid rgba(102, 126, 234, 0.1)"
                          }}
                        >
                          <FontAwesomeIcon icon={getConversationStyling(activeConversation.agentType).icon} className="text-white" style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <h5 className="mb-1 text-body-emphasis fw-bold" style={{ fontSize: "1.1rem" }}>
                            {activeConversation.name}
                          </h5>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon 
                              icon={faCircle} 
                              className="text-success me-2" 
                              style={{ fontSize: "0.5rem" }}
                            />
                            <small className="text-success fw-semibold" style={{ fontSize: "0.8rem" }}>
                              {activeConversation.agentType === 'human-collaborator' ? 'Team Member Chat' : 'AI Assistant Active'}
                            </small>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{
                            width: "48px",
                            height: "48px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            border: "3px solid rgba(102, 126, 234, 0.1)"
                          }}
                        >
                          <FontAwesomeIcon icon={faBolt} className="text-white" style={{ fontSize: "1.1rem" }} />
                        </div>
                        <div>
                          <h5 className="mb-1 text-body-emphasis fw-bold" style={{ fontSize: "1.1rem" }}>
                            Select a Conversation
                          </h5>
                          <div className="d-flex align-items-center">
                            <small className="text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
                              Choose an AI agent or team member to start chatting
                            </small>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Card.Header>
                
                <Card.Body className="d-flex flex-column p-0" style={{ backgroundColor: "#fafbfc" }}>
                  {/* Messages Container */}
                  <div 
                    ref={chatContainerRef}
                    className="flex-grow-1 p-4"
                    style={{ 
                      height: "calc(100vh - 300px)", 
                      overflowY: "auto",
                      background: "linear-gradient(180deg, #fafbfc 0%, #f8f9fa 100%)"
                    }}
                  >
                    {loadingChatHistory ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="text-center">
                          <div 
                            className="spinner-border text-primary mb-4"
                            style={{ width: "3rem", height: "3rem" }}
                          />
                          <p className="text-muted mb-0 fw-semibold">Loading conversation...</p>
                          <small className="text-muted">Please wait while we fetch your chat history</small>
                        </div>
                      </div>
                    ) : !activeConversation ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="text-center">
                          <FontAwesomeIcon icon={faMessage} className="text-muted mb-4" size="3x" />
                          <h4 className="text-muted mb-3">Start a Conversation</h4>
                          <p className="text-muted mb-4">Choose an AI assistant or chat with a team member</p>
                          <Button variant="primary" onClick={startNewConversation}>
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            New Conversation
                          </Button>
                        </div>
                      </div>
                    ) : (
                      messages.map(renderMessage)
                    )}
                    
                    {loading && (
                      <div className="d-flex justify-content-start mb-4">
                        <div className="d-flex align-items-start" style={{ maxWidth: "85%" }}>
                          <div 
                            className="bg-white p-3 border shadow-sm" 
                            style={{ 
                              borderRadius: "18px 18px 18px 6px",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)"
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div className="spinner-border spinner-border-sm text-primary me-3" />
                              <span className="text-muted fw-semibold">
                                {activeConversation?.agentType === 'human-collaborator' ? 'Sending message...' : 'AI is thinking...'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Input Area */}
                  <div 
                    className="border-top p-4"
                    style={{ 
                      backgroundColor: "#ffffff",
                      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.04)"
                    }}
                  >
                    <div className="position-relative">
                      <div className="position-relative">
                        <Form.Control
                          as="textarea"
                          rows={1}
                          placeholder={activeConversation 
                            ? activeConversation.agentType === 'human-collaborator' 
                              ? `Message ${activeConversation.participantName}...`
                              : "Ask me anything about your leads, market insights, or CRM optimization..."
                            : "Select a conversation to start messaging..."
                          }
                          value={inputMessage}
                          onChange={(e) => {
                            setInputMessage(e.target.value);
                            // Auto-resize textarea
                            e.target.style.height = "auto";
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && !e.shiftKey && !loading && inputMessage.trim() && activeConversation) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          className="border-0 shadow-sm"
                          style={{ 
                            minHeight: "56px",
                            borderRadius: "28px",
                            paddingLeft: "24px",
                            paddingRight: "80px",
                            paddingTop: "16px",
                            paddingBottom: "16px",
                            fontSize: "0.95rem",
                            backgroundColor: "#f8f9fa",
                            boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.06)",
                            resize: "none"
                          }}
                          disabled={loading || !activeConversation}
                        />
                        <Button
                          variant="primary"
                          onClick={handleSendMessage}
                          disabled={loading || !inputMessage.trim() || !activeConversation}
                          className="rounded-circle position-absolute end-0 top-50 translate-middle-y me-2 shadow-sm d-flex align-items-center justify-content-center"
                          style={{ 
                            width: "48px", 
                            height: "48px", 
                            zIndex: 10,
                            background: inputMessage.trim() && !loading && activeConversation
                              ? "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)"
                              : undefined,
                            border: "none",
                            boxShadow: "0 4px 12px rgba(13, 110, 253, 0.3)"
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: "100%" }}>
                            {loading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "1rem" }} />
                            )}
                          </div>
                        </Button>
                      </div>
                      
                      {error && (
                        <div className="text-danger mt-3 d-flex align-items-center" style={{ fontSize: "0.875rem" }}>
                          <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                          {formatErrorForReact(error)}
                        </div>
                      )}
                    </div>
                    
                    <div className="d-flex align-items-center justify-content-center mt-3">
                      <small 
                        className="text-muted fw-medium"
                        style={{ fontSize: "0.8rem", letterSpacing: "0.3px" }}
                      >
                        Powered by KAIROS IQ AI • Your data is secure and private
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <AgentSelectionModal />
        </div>
      )}
      {!isAuthorized && <Backdrop />}
    </>
  );
};

export default Chat;
